import type {
  ExpressionSpecification,
  LineLayerSpecification,
  SymbolLayerSpecification,
  VectorSourceSpecification,
} from 'maplibre-gl';

export const FREIGHT_RESTRICTIONS_SOURCE_ID = 'freight-restrictions';
export const FREIGHT_RESTRICTIONS_SOURCE_LAYER = 'freight_restrictions';
export const FREIGHT_RESTRICTIONS_LINE_LAYER_ID = 'freight-restrictions-line';
export const FREIGHT_RESTRICTIONS_HOVER_LAYER_ID = 'freight-restrictions-hover';
export const FREIGHT_RESTRICTIONS_SIGN_LAYER_ID = 'freight-restrictions-sign';

/**
 * Smallest zoom where the layer becomes visible. Below this the backend
 * returns `204 No Content` to keep PostGIS from scanning ~600k geometries
 * for empty bboxes.
 */
export const FREIGHT_RESTRICTIONS_MIN_ZOOM = 9;

/**
 * Per-kind colour palette. Hard prohibitions (red) are visually distinct
 * from numeric limits (orange/amber). Stays in sync with the legend
 * swatch in `MapLegend.tsx` (uses the HGV_NO red as the layer marker).
 */
export const COLOR_HGV_NO = '#cf1322';
export const COLOR_ACCESS_NO = '#ad2102';
export const COLOR_MOTOR_VEHICLE_NO = '#d4380d';
export const COLOR_HAZMAT_NO = '#722ed1';
export const COLOR_MAXWEIGHT = '#fa8c16';
export const COLOR_MAXHEIGHT = '#d48806';
export const COLOR_MAXWIDTH = '#d4b106';
export const COLOR_MAXLENGTH = '#7cb305';
export const COLOR_MAXAXLELOAD = '#bf8700';
export const COLOR_OTHER = '#8c8c8c';

const COLOR_BY_KIND: ExpressionSpecification = [
  'match', ['get', 'kind'],
  'hgv_no', COLOR_HGV_NO,
  'access_no', COLOR_ACCESS_NO,
  'motor_vehicle_no', COLOR_MOTOR_VEHICLE_NO,
  'hazmat_no', COLOR_HAZMAT_NO,
  'maxweight', COLOR_MAXWEIGHT,
  'maxheight', COLOR_MAXHEIGHT,
  'maxwidth', COLOR_MAXWIDTH,
  'maxlength', COLOR_MAXLENGTH,
  'maxaxleload', COLOR_MAXAXLELOAD,
  /* default */ COLOR_OTHER,
];

/**
 * Maps each `kind` to the `addImage()` icon id loaded by
 * `loadFreightRestrictionIcons`. Symbols only render at zoom ≥ 13 so
 * the line styling carries the message at city/region scale.
 *
 * `maxweight` uses the generic `sign-numeric` template — a red
 * annular ring with a blank white interior — and the actual tonnage
 * is overlaid by the symbol layer's `text-field` (see `VALUE_BY_KIND`).
 * This keeps the map in sync with the data; previously the layer
 * pointed at `sign-3.11.svg`, which had "7т" baked into its glyph
 * paths and therefore always displayed 7 regardless of `maxweight`.
 *
 * The other numeric kinds (`maxheight`/`maxwidth`/`maxlength`/
 * `maxaxleload`) still reference their pre-baked SVGs and inherit
 * the same baked-digit issue — migrating them is a known follow-up.
 */
const ICON_BY_KIND: ExpressionSpecification = [
  'match', ['get', 'kind'],
  'hgv_no', 'sign-3.4',
  'access_no', 'sign-3.2',
  'motor_vehicle_no', 'sign-3.3',
  'hazmat_no', 'sign-3.32',
  'maxweight', 'sign-numeric',
  'maxheight', 'sign-3.13',
  'maxwidth', 'sign-3.14',
  'maxlength', 'sign-3.15',
  'maxaxleload', 'sign-3.12',
  /* default */ '',
];

/**
 * Text label overlaid on the generic ring for the kinds that use
 * `sign-numeric`. Currently only `maxweight`; appends the tonnage
 * unit `"т"`. Every other `kind` returns the empty string so the
 * renderer draws no label on top of the pre-baked icons (whose own
 * digits already live inside the SVG, see ICON_BY_KIND comment).
 *
 * `to-string` formats whole numbers as `"8"` and fractional values
 * as `"3.5"`. The decimal separator stays as a point; switching to
 * the Russian convention `"8,5 т"` would require `number-format`
 * with an explicit locale and is intentionally out of scope.
 */
const VALUE_BY_KIND: ExpressionSpecification = [
  'case',
  ['==', ['get', 'kind'], 'maxweight'],
    ['concat', ['to-string', ['get', 'maxweight']], ' т'],
  '',
];

const LINE_WIDTH: ExpressionSpecification = [
  'interpolate', ['linear'], ['zoom'],
  9, 1.5,
  12, 2.5,
  16, 4,
];

const HOVER_WIDTH: ExpressionSpecification = [
  'interpolate', ['linear'], ['zoom'],
  9, 4,
  12, 6,
  16, 8,
];

const SIGN_ICON_SIZE: ExpressionSpecification = [
  'interpolate', ['linear'], ['zoom'],
  13, 0.36,
  16, 0.60,
  18, 0.80,
];

/**
 * Label text size in pixels, matched to `SIGN_ICON_SIZE` so the digits
 * scale with the ring at the same rate. Numbers from OSM Russia top out
 * at 4 characters (`"10 т"` / `"3.5 м"`), so a font that's ~38% of the
 * icon height fits with breathing room.
 */
const SIGN_TEXT_SIZE: ExpressionSpecification = [
  'interpolate', ['linear'], ['zoom'],
  13, 9,
  16, 15,
  18, 22,
];

export function buildFreightRestrictionsSource(
  apiBaseUrl: string,
): VectorSourceSpecification {
  return {
    type: 'vector',
    tiles: [
      `${apiBaseUrl}/api/v1/tiles/freight-restrictions/{z}/{x}/{y}.mvt`,
    ],
    minzoom: FREIGHT_RESTRICTIONS_MIN_ZOOM,
    maxzoom: 22,
  };
}

export function buildFreightRestrictionsLineLayer(): LineLayerSpecification {
  return {
    id: FREIGHT_RESTRICTIONS_LINE_LAYER_ID,
    type: 'line',
    source: FREIGHT_RESTRICTIONS_SOURCE_ID,
    'source-layer': FREIGHT_RESTRICTIONS_SOURCE_LAYER,
    minzoom: FREIGHT_RESTRICTIONS_MIN_ZOOM,
    layout: { 'line-cap': 'round', 'line-join': 'round' },
    paint: {
      'line-color': COLOR_BY_KIND,
      'line-width': LINE_WIDTH,
      'line-opacity': 0.85,
      // A subtle dash pattern makes restricted segments stand out from
      // the basemap roads they overlay without burying them.
      'line-dasharray': [2, 1],
    },
  };
}

/**
 * Wider, fully opaque overlay drawn on top of the base layer when the
 * user hovers a feature. Filter is updated by `MainMap` from
 * `mouseenter`/`mouseleave` events.
 */
export function buildFreightRestrictionsHoverLayer(): LineLayerSpecification {
  return {
    id: FREIGHT_RESTRICTIONS_HOVER_LAYER_ID,
    type: 'line',
    source: FREIGHT_RESTRICTIONS_SOURCE_ID,
    'source-layer': FREIGHT_RESTRICTIONS_SOURCE_LAYER,
    minzoom: FREIGHT_RESTRICTIONS_MIN_ZOOM,
    filter: ['==', ['get', 'way_id'], -1],
    layout: { 'line-cap': 'round', 'line-join': 'round' },
    paint: {
      'line-color': COLOR_BY_KIND,
      'line-width': HOVER_WIDTH,
      'line-opacity': 1,
    },
  };
}

/**
 * Symbol layer that places a Russian PDD sign icon along each segment.
 * Only renders at z ≥ 13 — at smaller zooms a single tile may carry
 * thousands of features and the symbols become unreadable noise.
 *
 * For numeric-limit kinds the icon is a generic ring template and the
 * label (`VALUE_BY_KIND`) renders the actual value + unit on top of it.
 * `text-allow-overlap` / `text-ignore-placement` are enabled so the
 * label always travels with its icon — without them the renderer can
 * suppress the digit when the icon is kept (icon-text decoupling).
 */
export function buildFreightRestrictionsSignLayer(): SymbolLayerSpecification {
  return {
    id: FREIGHT_RESTRICTIONS_SIGN_LAYER_ID,
    type: 'symbol',
    source: FREIGHT_RESTRICTIONS_SOURCE_ID,
    'source-layer': FREIGHT_RESTRICTIONS_SOURCE_LAYER,
    minzoom: 13,
    filter: ['!=', ICON_BY_KIND, ''],
    layout: {
      'icon-image': ICON_BY_KIND,
      'icon-size': SIGN_ICON_SIZE,
      'icon-allow-overlap': false,
      'icon-ignore-placement': false,
      'symbol-placement': 'line-center',
      'symbol-spacing': 250,
      // Glyph font supported by the OpenFreeMap Liberty basemap's glyph
      // server (https://tiles.openfreemap.org/fonts/...). If the basemap
      // is ever swapped, verify this font is still served — otherwise
      // labels silently disappear.
      'text-field': VALUE_BY_KIND,
      'text-font': ['Noto Sans Bold'],
      'text-size': SIGN_TEXT_SIZE,
      'text-anchor': 'center',
      'text-offset': [0, 0],
      'text-allow-overlap': true,
      'text-ignore-placement': true,
      'text-padding': 0,
    },
    paint: {
      'icon-opacity': 0.95,
      'text-color': '#000000',
      'text-halo-color': '#ffffff',
      'text-halo-width': 1.2,
      'text-halo-blur': 0.4,
    },
  };
}
